import { pdfService } from "./pdfService";

export const executionService = {
  // Create a new execution workflow
  async initiateExecution(graphService, {
    contractId,
    contractRef,
    contractTitle,
    approvers, // Array of { displayName, mail }
    initiatedBy,
    driveId,
    contractDraftPath
  }) {
    try {
      // 1. VALIDATE: Check if PDF can be generated FIRST - don't create any records until PDF is ready
      if (!driveId || !contractDraftPath) {
        throw new Error('Missing driveId or contractDraftPath - cannot generate PDF');
      }

      console.log('[executionService] 🔄 Step 1: Generating PDF from contract draft...');
      console.log('[executionService] Path:', contractDraftPath);

      // Generate PDF BEFORE creating any execution records
      let pdfBytes = null;
      let pdfFileName = null;
      try {
        pdfBytes = await pdfService.convertDraftToPdf(
          graphService,
          driveId,
          contractDraftPath,
          contractTitle
        );
        pdfFileName = pdfService.getExecutedPdfName(contractTitle);
        console.log('[executionService] ✅ PDF Generated:', pdfFileName, '- Bytes:', pdfBytes?.byteLength);
      } catch (pdfError) {
        console.error('[executionService] ❌ PDF Generation Failed:', pdfError.message);
        throw new Error(`Cannot generate PDF: ${pdfError.message}`);
      }

      // Only proceed if PDF was generated successfully
      if (!pdfBytes) {
        throw new Error('PDF generation produced no data');
      }

      // 1a. Auto-append admin signature to PDF if available
      console.log('[executionService] 🔄 Step 1.5: Checking for admin signature...');
      try {
        const adminSignatures = await this.getApproverSignatures(graphService, initiatedBy);
        if (adminSignatures && adminSignatures.length > 0) {
          const latestSignature = adminSignatures[0]; // Get most recent
          console.log('[executionService] 📝 Found admin signature, appending to PDF...');
          pdfBytes = await pdfService.appendAdminSignatureToAllPages(
            pdfBytes,
            latestSignature.SignatureData
          );
          console.log('[executionService] ✅ Admin signature appended to PDF');
        } else {
          console.log('[executionService] ℹ️ No admin signature found, skipping auto-append');
        }
      } catch (sigError) {
        console.warn('[executionService] ⚠️ Could not append admin signature:', sigError.message);
        // Continue without admin signature - not critical
      }

      // 1b. Save initial PDF to SharePoint
      console.log('[executionService] 🔄 Step 2: Saving PDF to SharePoint...');
      try {
        const folderPath = contractDraftPath.substring(0, contractDraftPath.lastIndexOf('/'));
        await pdfService.savePdfToSharePoint(
          graphService,
          driveId,
          folderPath,
          pdfBytes,
          pdfFileName
        );
        console.log('[executionService] ✅ PDF Saved to SharePoint');
      } catch (saveError) {
        console.error('[executionService] ❌ Failed to save PDF:', saveError.message);
        throw new Error(`Cannot save PDF to SharePoint: ${saveError.message}`);
      }

      // 2. NOW create Execution record (only after PDF is confirmed)
      console.log('[executionService] 🔄 Step 3: Creating execution record...');
      const execution = await graphService.createListItem("Executions", {
        Title: `Execution - ${contractRef}`,
        ContractID: contractId,
        ContractRefNo: contractRef,
        ContractTitle: contractTitle,
        Status: "In Progress",
        InitiatedBy: initiatedBy,
        InitiatedAt: new Date().toISOString(),
        TotalApprovers: approvers.length,
        ApproversCompleted: 0
      });

      const executionId = execution.id;
      console.log('[executionService] ✅ Execution Created:', executionId);

      // 3. Create ExecutionApprovers records for each approver
      const approverRecords = [];
      for (let i = 0; i < approvers.length; i++) {
        const approver = approvers[i];
        const record = await graphService.createListItem("ExecutionApprovers", {
          Title: `${contractRef} - Approver ${i + 1}`,
          ExecutionID: executionId,
          ContractID: contractId,
          ContractRefNo: contractRef,
          ApproverName: approver.displayName,
          ApproverEmail: approver.mail,
          ApprovalOrder: i + 1,
          Status: i === 0 ? "Pending" : "Waiting",
          CreatedAt: new Date().toISOString()
        });
        approverRecords.push({ ...record, index: i });
      }

      console.log('[executionService] ✅ Created', approverRecords.length, 'approver records');

      return {
        executionId,
        approverRecords,
        totalApprovers: approvers.length,
        pdfGenerated: !!pdfBytes
      };
    } catch (error) {
      console.error('[executionService] Error initiating execution:', error);
      throw error;
    }
  },

  // Get current approver for execution
  async getCurrentApprover(graphService, executionId) {
    try {
      const data = await graphService.getListItems(
        "ExecutionApprovers",
        `fields/ExecutionID eq '${executionId}' and fields/Status eq 'Pending'`
      );

      if (data?.value?.length > 0) {
        return { ...data.value[0].fields, id: data.value[0].id };
      }
      return null;
    } catch (error) {
      console.error('[executionService] Error getting current approver:', error);
      return null;
    }
  },

  // Get execution details
  async getExecution(graphService, executionId) {
    try {
      const execution = await graphService.getListItem("Executions", executionId);
      return { ...execution.fields, id: execution.id };
    } catch (error) {
      console.error('[executionService] Error getting execution:', error);
      return null;
    }
  },

  // Get all approvers for execution
  async getExecutionApprovers(graphService, executionId) {
    try {
      const data = await graphService.getListItems(
        "ExecutionApprovers",
        `fields/ExecutionID eq '${executionId}'`
      );

      return (data?.value || [])
        .map(item => ({ ...item.fields, id: item.id }))
        .sort((a, b) => (a.ApprovalOrder || 0) - (b.ApprovalOrder || 0));
    } catch (error) {
      console.error('[executionService] Error getting approvers:', error);
      return [];
    }
  },

  // Save approver signature
  async saveApproverSignature(graphService, {
    approverId,
    approverEmail,
    signatureBase64,
    signatureFileName
  }) {
    try {
      // One signature per approver email, ever — update the existing record
      // instead of creating a new one each time someone approves. Without
      // this check, every single approval (across every contract) added a
      // fresh duplicate for the same person.
      const existing = await graphService.getListItems(
        "ApproverSignatures",
        `fields/ApproverEmail eq '${approverEmail}'`
      );
      // Duplicates may already exist from before this fix — pick the most
      // recent one rather than whatever order the API happens to return.
      const existingRecord = (existing?.value || [])
        .sort((a, b) => new Date(b.fields?.CreatedAt || 0) - new Date(a.fields?.CreatedAt || 0))[0];

      const fields = {
        Title: `${approverEmail} - ${new Date().toLocaleDateString()}`,
        ApproverEmail: approverEmail,
        SignatureFileName: signatureFileName,
        SignatureData: signatureBase64,
        CreatedAt: new Date().toISOString()
      };

      let signature;
      if (existingRecord) {
        await graphService.updateListItem("ApproverSignatures", existingRecord.id, fields);
        signature = { id: existingRecord.id, ...fields };
        console.log('[executionService] ✅ Updated existing signature:', signature.id);
      } else {
        signature = await graphService.createListItem("ApproverSignatures", fields);
        console.log('[executionService] ✅ Saved new signature:', signature.id);
      }
      return signature;
    } catch (error) {
      console.error('[executionService] Error saving signature:', error);
      throw error;
    }
  },

  // Get approver's previous signatures
  async getApproverSignatures(graphService, approverEmail) {
    try {
      const data = await graphService.getListItems(
        "ApproverSignatures",
        `fields/ApproverEmail eq '${approverEmail}'`
      );

      return (data?.value || [])
        .map(item => ({ ...item.fields, id: item.id }))
        .sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));
    } catch (error) {
      console.error('[executionService] Error getting signatures:', error);
      return [];
    }
  },

  // Mark approver as signed/approved
  async approveExecution(graphService, {
    approverId,
    executionId,
    approverEmail,
    signatureId,
    comments,
    signatureData,
    signaturePage,
    signatureX,
    signatureY,
    signatureWidth,
    signatureHeight,
    approverName,
    textBoxes = [],
    driveId,
    contractFolder
  }) {
    try {
      // Update ExecutionApprovers record
      const approvalTime = new Date().toISOString();
      await graphService.updateListItem("ExecutionApprovers", approverId, {
        Status: "Approved",
        SignatureID: signatureId,
        ApprovedAt: approvalTime,
        Comments: comments
      });

      // Get execution and count completed approvals
      const approvers = await this.getExecutionApprovers(graphService, executionId);
      const execution = await this.getExecution(graphService, executionId);
      const completed = approvers.filter(a => a.Status === "Approved").length;

      // Update Execution record
      await graphService.updateListItem("Executions", executionId, {
        ApproversCompleted: completed
      });

      // Embed signature on PDF if provided
      if (signatureData && driveId && contractFolder) {
        console.log('[executionService] 🔄 Embedding signature on PDF...');
        console.log('[executionService] Signature data length:', signatureData?.length);
        console.log('[executionService] Page:', signaturePage, 'Folder:', contractFolder);

        try {
          const pdfFileName = pdfService.getExecutedPdfName(execution.ContractTitle);
          console.log('[executionService] PDF filename:', pdfFileName);

          // Get current PDF from SharePoint
          let pdfBytes;
          try {
            console.log('[executionService] Downloading PDF from:', `${contractFolder}/${pdfFileName}.pdf`);
            const response = await graphService.downloadFile(driveId, `${contractFolder}/${pdfFileName}.pdf`);
            pdfBytes = response;
            console.log('[executionService] ✅ Downloaded PDF, size:', pdfBytes?.byteLength);
          } catch (downloadError) {
            console.warn('[executionService] Could not download PDF:', downloadError.message);
          }

          // Embed the signature with position
          if (pdfBytes) {
            console.log('[executionService] Embedding signature at position:', { x: signatureX, y: signatureY, width: signatureWidth, height: signatureHeight });
            const signedPdfBytes = await pdfService.embedSignatureOnPdf(
              pdfBytes,
              signatureData,
              signaturePage || 1,
              signatureX || 50,
              signatureY || 130,
              signatureWidth || 300,
              signatureHeight || 120
            );
            console.log('[executionService] ✅ Signature embedded, size:', signedPdfBytes?.byteLength);

            // Add text boxes to PDF if any exist
            let pdfWithText = signedPdfBytes;
            if (textBoxes && textBoxes.length > 0) {
              console.log('[executionService] 📝 Adding', textBoxes.length, 'text boxes to PDF');
              pdfWithText = await pdfService.addTextBoxesToPdf(
                signedPdfBytes,
                textBoxes,
                signaturePage || 1
              );
              console.log('[executionService] ✅ Text boxes added to PDF');
            } else {
              console.log('[executionService] ℹ️ No text boxes to add');
            }

            // Upload updated PDF back to SharePoint
            console.log('[executionService] Uploading PDF back to SharePoint...');
            await graphService.uploadFile(
              driveId,
              contractFolder,
              `${pdfFileName}.pdf`,
              pdfWithText,
              "application/pdf"
            );

            console.log('[executionService] ✅ Saved signed PDF with signature from', approverEmail);
          } else {
            console.warn('[executionService] ⚠️ PDF bytes not available, skipping signature embedding');
          }
        } catch (pdfError) {
          console.error('[executionService] ❌ Error updating PDF with signature:', pdfError);
          console.error('[executionService] Error details:', pdfError.message);
        }
      } else {
        console.warn('[executionService] Missing signature data, driveId, or contractFolder for PDF embedding');
        console.log('[executionService] signatureData:', !!signatureData, 'driveId:', !!driveId, 'contractFolder:', contractFolder);
      }

      // Check if all approved
      if (completed === approvers.length) {
        // All signed - mark as complete
        const completedAt = new Date().toISOString();
        await graphService.updateListItem("Executions", executionId, {
          Status: "Completed",
          CompletedAt: completedAt
        });

        // Mark contract as Executed
        await graphService.updateListItem("Contracts", execution.ContractID, {
          Status: "Executed",
          ExecutionDate: completedAt,
          ExecutedPdfFile: pdfService.getExecutedPdfName(execution.ContractTitle)
        });

        console.log('[executionService] ✅ All approvers signed - execution complete');
        console.log('[executionService] ✅ Contract marked as Executed');

        // Notify all admins that execution is complete
        try {
          await this.notifyExecutionComplete(graphService, {
            contractRef: execution.ContractRefNo,
            contractTitle: execution.ContractTitle,
            approversCount: execution.TotalApprovers,
            completedAt: completedAt
          });
        } catch (notifyError) {
          console.error('[executionService] ⚠️ Failed to send completion notification:', notifyError);
          // Don't throw - notification failure shouldn't block execution completion
        }
      } else {
        // Notify next approver
        const nextApprover = approvers.find(a => a.Status === "Waiting");
        if (nextApprover) {
          await graphService.updateListItem("ExecutionApprovers", nextApprover.id, {
            Status: "Pending"
          });
          console.log('[executionService] ✅ Next approver status updated:', nextApprover.ApproverEmail);

          // Send approval reminder email to next approver
          try {
            await this.sendApprovalReminder(graphService, {
              approverEmail: nextApprover.ApproverEmail,
              approverName: nextApprover.ApproverName,
              contractRef: execution.ContractRefNo,
              contractTitle: execution.ContractTitle,
              executionId: executionId
            });
            console.log('[executionService] ✅ Approval email sent to next approver:', nextApprover.ApproverEmail);
          } catch (emailError) {
            console.error('[executionService] ⚠️ Failed to send approval email to next approver:', emailError);
            // Don't throw - email failure shouldn't block the approval process
          }
        }
      }
    } catch (error) {
      console.error('[executionService] Error approving execution:', error);
      throw error;
    }
  },

  // Reject execution
  async rejectExecution(graphService, {
    approverId,
    reason
  }) {
    try {
      await graphService.updateListItem("ExecutionApprovers", approverId, {
        Status: "Rejected",
        RejectionReason: reason,
        RejectedAt: new Date().toISOString()
      });

      console.log('[executionService] ✅ Execution rejected');
    } catch (error) {
      console.error('[executionService] Error rejecting execution:', error);
      throw error;
    }
  },

  // Skip approver
  async skipApprover(graphService, {
    approverId,
    executionId,
    skipReason
  }) {
    try {
      await graphService.updateListItem("ExecutionApprovers", approverId, {
        Status: "Skipped",
        SkipReason: skipReason,
        SkippedAt: new Date().toISOString()
      });

      // Notify next approver
      const approvers = await this.getExecutionApprovers(graphService, executionId);
      const nextApprover = approvers.find(a => a.Status === "Waiting");
      if (nextApprover) {
        await graphService.updateListItem("ExecutionApprovers", nextApprover.id, {
          Status: "Pending"
        });
      }

      console.log('[executionService] ✅ Approver skipped');
    } catch (error) {
      console.error('[executionService] Error skipping approver:', error);
      throw error;
    }
  },

  // Get all executions
  async getExecutions(graphService, filter = "") {
    try {
      const data = await graphService.getListItems("Executions", filter);
      return (data?.value || []).map(item => ({ ...item.fields, id: item.id }));
    } catch (error) {
      console.error('[executionService] Error getting executions:', error);
      return [];
    }
  },

  // Complete execution and update contract status
  async completeExecution(graphService, {
    executionId,
    contractId,
    contractRef,
    contractTitle,
    pdfFileName,
    pdfWebUrl
  }) {
    try {
      // Update execution status
      await graphService.updateListItem("Executions", executionId, {
        Status: "Completed",
        CompletedAt: new Date().toISOString(),
        ExecutedPdfFile: pdfFileName,
        ExecutedPdfUrl: pdfWebUrl
      });

      // Update contract status to "Executed"
      await graphService.updateListItem("Contracts", contractId, {
        Status: "Executed",
        ExecutionDate: new Date().toISOString(),
        ExecutedPdfFile: pdfFileName
      });

      console.log('[executionService] ✅ Execution completed and contract marked as executed');

      return {
        executionId,
        contractId,
        status: "Executed"
      };
    } catch (error) {
      console.error('[executionService] Error completing execution:', error);
      throw error;
    }
  },

  // Send completion notification to all admins
  async notifyExecutionComplete(graphService, {
    contractRef,
    contractTitle,
    approversCount,
    completedAt
  }) {
    try {
      // Fetch all admins
      const allUsers = await graphService.getListItems("AllowedUsers");
      const adminEmails = (allUsers?.value || [])
        .filter(u => u.fields.Role === 'admin' || u.fields.Role === 'Admin')
        .map(u => u.fields.Title)
        .filter(email => email && email.trim());

      if (adminEmails.length > 0) {
        const emailBody = `
          <p>Hi,</p>
          <p>The contract execution process has been completed successfully:</p>
          <div style="background-color: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <p><strong>Contract:</strong> ${contractTitle}</p>
            <p><strong>Reference:</strong> ${contractRef}</p>
            <p><strong>Approvers:</strong> ${approversCount}</p>
            <p><strong>Completed:</strong> ${new Date(completedAt).toLocaleString()}</p>
          </div>
          <p>All required approvers have signed the contract. The signed document is now available in the contract folder.</p>
          <p>Log in to LexCore to download the executed document.</p>
          <p>Best regards,<br/>LexCore Contract Management System</p>
        `;

        await graphService.sendEmail(
          adminEmails,
          `[LexCore] Contract Execution Complete: ${contractRef}`,
          emailBody
        );

        console.log('[executionService] ✅ Completion notification sent to', adminEmails.length, 'admin(s):', adminEmails);
      } else {
        console.warn('[executionService] ⚠️ No admins found to notify');
      }
    } catch (error) {
      console.error('[executionService] Error sending completion notification:', error);
      // Don't throw - notification failure shouldn't block execution completion
    }
  },

  // Send reminder email to pending approver
  async sendApprovalReminder(graphService, {
    approverEmail,
    approverName,
    contractRef,
    contractTitle,
    executionId
  }) {
    try {
      const executionLink = `${window.location.origin}/approver/${executionId}/${executionId}`;

      const emailBody = `
        <p>Hi ${approverName},</p>
        <p>This is a reminder that you have a pending contract approval:</p>
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0;">
          <p><strong>Contract:</strong> ${contractTitle}</p>
          <p><strong>Reference:</strong> ${contractRef}</p>
        </div>
        <p>Please review and sign the contract at your earliest convenience:</p>
        <p style="text-align: center; margin: 20px 0;">
          <a href="${executionLink}" style="display: inline-block; padding: 12px 24px; background-color: #ca8a04; color: white; text-decoration: none; border-radius: 6px; font-weight: bold;">
            Review & Sign Contract
          </a>
        </p>
        <p>Best regards,<br/>LexCore Contract Management System</p>
      `;

      await graphService.sendEmail(
        [approverEmail],
        `[LexCore Reminder] Contract Approval Pending: ${contractRef}`,
        emailBody
      );

      console.log('[executionService] ✅ Reminder email sent to', approverEmail);
      return true;
    } catch (error) {
      console.error('[executionService] Error sending reminder email:', error);
      throw error;
    }
  }
};
